/**
 * The design-system preview flags, in one place.
 *
 * Two things read and write these: `DsNextFlag`, which arms them from a URL
 * param on load, and the control on `/prototypes`, which is where a person
 * actually flips them. Both go through here so the keys, the class names and
 * the rules for applying them exist once — two copies of "which class means
 * geometric" is the kind of drift that ends with a switch that half works.
 *
 * Everything is per-browser and client-only. There is no server, no session
 * and no user check: a `localStorage` key is already private to one machine,
 * and asking the session who you are would turn a preview into an
 * authorisation question.
 *
 * Every storage call is wrapped. Private mode and blocked site data make
 * `localStorage` throw rather than return null, and a preview flag is never
 * worth taking a page down for — so a failure means "not remembered", never
 * an error.
 */

export const DS_KEY = "nf-ds-next";
export const FONT_KEY = "nf-ds-font";

export const DS_CLASS = "ds-next";

/** The candidate typefaces, and the class that switches each one on. */
export const FONT_CLASSES = {
  geometric: "ds-font-geometric",
  instrument: "ds-font-instrument",
} as const;

export type FontChoice = keyof typeof FONT_CLASSES;

/** `font: null` is today's faces — the third state, not the absence of one. */
export type Flags = { next: boolean; font: FontChoice | null };

export function isFontChoice(value: string | null): value is FontChoice {
  return value !== null && value in FONT_CLASSES;
}

export function readFlags(): Flags {
  try {
    const stored = window.localStorage.getItem(FONT_KEY);
    return {
      next: window.localStorage.getItem(DS_KEY) === "1",
      font: isFontChoice(stored) ? stored : null,
    };
  } catch {
    return { next: false, font: null };
  }
}

export function writeFlags(flags: Flags): void {
  try {
    if (flags.next) window.localStorage.setItem(DS_KEY, "1");
    else window.localStorage.removeItem(DS_KEY);

    if (flags.font) window.localStorage.setItem(FONT_KEY, flags.font);
    else window.localStorage.removeItem(FONT_KEY);
  } catch {
    // Not remembered across reloads; `applyFlags` still takes effect now.
  }
}

/**
 * Put the flags on `<html>`.
 *
 * Every font class is toggled on every call rather than only the chosen one
 * added — switching from geometric to instrument has to remove the first, and
 * "set the new one" without "clear the old" leaves both on the element with
 * the cascade picking the winner by source order.
 */
export function applyFlags(flags: Flags): void {
  const root = document.documentElement;
  root.classList.toggle(DS_CLASS, flags.next);
  for (const [choice, className] of Object.entries(FONT_CLASSES)) {
    root.classList.toggle(className, flags.font === choice);
  }
}
